import { useEffect, useState, useCallback } from "react";
import { Store, Plus, Trash2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";

export default function StorePoolsTab({ contestId }) {
  const [stores, setStores] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [pools, setPools] = useState([]);
  const [sel, setSel] = useState("");
  const [edit, setEdit] = useState({});

  const load = useCallback(async () => {
    const [s, p, c] = await Promise.all([api.listStores(), api.listPrizes(contestId), api.listContestStores(contestId)]);
    setStores(s);
    setPrizes(p);
    setPools(c);
    setEdit(Object.fromEntries(c.map((x) => [x.store_id, x.prizes || {}])));
  }, [contestId]);
  useEffect(() => {
    load();
  }, [load]);

  const assigned = new Set(pools.map((p) => p.store_id));
  const free = stores.filter((s) => !assigned.has(s.id));

  const add = async () => {
    if (!sel) return toast.error("Seleziona una sede");
    await api.upsertContestStore(contestId, sel, {});
    setSel("");
    toast.success("Sede assegnata: imposta il montepremi");
    load();
  };
  const setLim = (sid, pid, k, v) => setEdit((e) => ({ ...e, [sid]: { ...(e[sid] || {}), [pid]: { ...((e[sid] || {})[pid] || {}), [k]: v === "" ? null : Number(v) } } }));
  const save = async (sid) => {
    await api.upsertContestStore(contestId, sid, edit[sid] || {});
    toast.success("Montepremi sede salvato");
    load();
  };
  const remove = async (sid) => {
    if (!window.confirm("Rimuovere la sede dal concorso? Tornerà al montepremi generale.")) return;
    await api.deleteContestStore(contestId, sid);
    load();
  };

  return (
    <div className="space-y-5" data-testid="store-pools-tab">
      <Card className="p-5 bg-card border-border">
        <h3 className="font-display font-bold text-lg flex items-center gap-2 mb-2"><Store className="h-4 w-4" /> Assegna concorso ai punti vendita</h3>
        <p className="text-xs text-muted-foreground mb-3">
          Ogni sede assegnata ha un montepremi individuale (quantità giornaliera e/o totale per premio). Le giocate vengono attribuite alla sede tramite il lotto di codici oppure il totem associato (link <span className="font-mono-code">/gioca/{contestId}?sede=ID</span>). Le sedi non assegnate usano il montepremi generale.
        </p>
        <div className="flex gap-2">
          <select data-testid="pool-store-select" value={sel} onChange={(e) => setSel(e.target.value)} className="flex-1 h-10 rounded-md border border-border bg-background px-3 text-sm">
            <option value="">Seleziona sede…</option>
            {free.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <Button data-testid="assign-store-button" onClick={add} className="gap-2"><Plus className="h-4 w-4" /> Assegna</Button>
        </div>
        {stores.length === 0 && <p className="text-xs text-amber-400 mt-2">Nessuna sede: creale da "Punti Vendita".</p>}
      </Card>

      {pools.map((pool) => (
        <Card key={pool.store_id} data-testid={`pool-card-${pool.store_id}`} className="p-5 bg-card border-border">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="font-semibold">{pool.store_name}</h4>
              <p className="text-xs text-muted-foreground font-mono-code">sede={pool.store_id}</p>
            </div>
            <div className="flex gap-2">
              <Button data-testid={`save-pool-${pool.store_id}`} size="sm" onClick={() => save(pool.store_id)} className="gap-1.5"><Save className="h-3.5 w-3.5" /> Salva</Button>
              <Button size="sm" variant="ghost" onClick={() => remove(pool.store_id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr><th className="py-1">Premio</th><th className="py-1 w-28">Al giorno</th><th className="py-1 w-28">Totale</th><th className="py-1 w-36">Vinti (oggi/tot)</th></tr>
            </thead>
            <tbody>
              {prizes.map((p) => {
                const lim = (edit[pool.store_id] || {})[p.id] || {};
                const won = pool.won?.[p.id];
                return (
                  <tr key={p.id} className="border-t border-border">
                    <td className="py-2">{p.name}</td>
                    <td className="py-2 pr-2"><Input data-testid={`pool-daily-${pool.store_id}-${p.id}`} type="number" min={0} value={lim.daily ?? ""} onChange={(e) => setLim(pool.store_id, p.id, "daily", e.target.value)} className="h-8" placeholder="—" /></td>
                    <td className="py-2 pr-2"><Input data-testid={`pool-total-${pool.store_id}-${p.id}`} type="number" min={0} value={lim.total ?? ""} onChange={(e) => setLim(pool.store_id, p.id, "total", e.target.value)} className="h-8" placeholder="—" /></td>
                    <td className="py-2 text-xs text-emerald-400">{won ? `${won.today} / ${won.total}` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[11px] text-muted-foreground mt-2">Lascia vuoti entrambi i campi per escludere il premio in questa sede.</p>
        </Card>
      ))}
    </div>
  );
}
